import { computed, type ComputedRef } from 'vue';
import { useStore } from 'vuex';
import type {
  BusinessVerificationCode,
  UserVerificationCode,
} from '@/data/verifications';
import {
  hasBusinessNameIssue as checkBusinessNameIssue,
  hasBusinessEINIssue as checkBusinessEINIssue,
  hasBusinessAddressIssue as checkBusinessAddressIssue,
  hasUserAddressIssue as checkUserAddressIssue,
  hasUserFirstNameIssue as checkUserFirstNameIssue,
  hasUserLastNameIssue as checkUserLastNameIssue,
  hasUserDOBIssue as checkUserDOBIssue,
  hasUserSSNIssue as checkUserSSNIssue,
} from '@/utils/verifications';

interface UseVerificationCodesReturn {
  // Raw verification codes
  userVerificationCodes: ComputedRef<UserVerificationCode[]>;
  businessVerificationCodes: ComputedRef<BusinessVerificationCode[]>;

  // Business verification computed properties
  hasBusinessNameIssue: ComputedRef<boolean>;
  hasBusinessEINIssue: ComputedRef<boolean>;
  hasBusinessAddressIssue: ComputedRef<boolean>;

  // User verification computed properties
  hasUserAddressIssue: ComputedRef<boolean>;
  hasUserFirstNameIssue: ComputedRef<boolean>;
  hasUserLastNameIssue: ComputedRef<boolean>;
  hasUserDOBIssue: ComputedRef<boolean>;
  hasUserSSNIssue: ComputedRef<boolean>;
}

/**
 * Composable for working with verification codes from the store
 * Provides computed properties for all verification code checks
 */
export function useVerificationCodes(): UseVerificationCodesReturn {
  const store = useStore();

  // Raw verification codes from store
  const userVerificationCodes: ComputedRef<UserVerificationCode[]> = computed(
    () => store.getters.userVerificationCodes || [],
  );
  const businessVerificationCodes: ComputedRef<BusinessVerificationCode[]> =
    computed(() => store.getters.businessVerificationCodes || []);

  // Business verification computed properties
  const hasBusinessNameIssue: ComputedRef<boolean> = computed(() =>
    checkBusinessNameIssue(businessVerificationCodes.value),
  );

  const hasBusinessEINIssue: ComputedRef<boolean> = computed(() =>
    checkBusinessEINIssue(businessVerificationCodes.value),
  );

  const hasBusinessAddressIssue: ComputedRef<boolean> = computed(() =>
    checkBusinessAddressIssue(businessVerificationCodes.value),
  );

  // User verification computed properties
  const hasUserAddressIssue: ComputedRef<boolean> = computed(() =>
    checkUserAddressIssue(userVerificationCodes.value),
  );

  const hasUserFirstNameIssue: ComputedRef<boolean> = computed(() =>
    checkUserFirstNameIssue(userVerificationCodes.value),
  );

  const hasUserLastNameIssue: ComputedRef<boolean> = computed(() =>
    checkUserLastNameIssue(userVerificationCodes.value),
  );

  const hasUserDOBIssue: ComputedRef<boolean> = computed(() =>
    checkUserDOBIssue(userVerificationCodes.value),
  );

  const hasUserSSNIssue: ComputedRef<boolean> = computed(() =>
    checkUserSSNIssue(userVerificationCodes.value),
  );

  return {
    // Raw codes
    userVerificationCodes,
    businessVerificationCodes,

    // Business checks
    hasBusinessNameIssue,
    hasBusinessEINIssue,
    hasBusinessAddressIssue,

    // User checks
    hasUserAddressIssue,
    hasUserFirstNameIssue,
    hasUserLastNameIssue,
    hasUserDOBIssue,
    hasUserSSNIssue,
  };
}

// Re-export the enums for convenience
export { BusinessVerificationCode, UserVerificationCode };
